import { createSlice } from "@reduxjs/toolkit";
import {
    getAllBankAccountsByCaseIdAction,
    getBankAccountByIdAction,
    createBankAccountAction,
    updateBankAccountAction,
    deleteBankAccountAction,
    searchBankAccountsAction,
    bulkCreateBankAccountsAction,
    getBankAccountsWithPaginationAction,
    exportBankAccountsToExcelAction,
    clearBankAccountStateAction,
    setSelectedBankAccountAction,
    setBankAccountFiltersAction
} from "../../actions/Case/caseBankAccountDetailsAssetAction";

const initialState = {
    bankAccounts: [],
    assetOwned: {},
    selectedBankAccount: null,
    filters: {},
    pagination: {
        page: 1,
        limit: 10,
        total: 0,
        pages: 1
    },
    loading: false,
    error: null,
    success: false,
    message: "",
    exportLoading: false,
    exportError: null,
    exportData: null
};

const bankAccountSlice = createSlice({
    name: "bankAccount",
    initialState,
    reducers: {
        resetBankAccountState: (state) => {
            return initialState;
        },
        clearBankAccountError: (state) => {
            state.error = null;
            state.message = "";
        },
        updateBankAccountInList: (state, action) => {
            const index = state.bankAccounts.findIndex(
                b => b.bankAccountID === action.payload.bankAccountID
            );
            if (index !== -1) {
                state.bankAccounts[index] = { ...state.bankAccounts[index], ...action.payload };
            }
        },
        removeBankAccountFromList: (state, action) => {
            state.bankAccounts = state.bankAccounts.filter(
                b => b.bankAccountID !== action.payload
            );
        },
        clearSelectedBankAccount: (state) => {
            state.selectedBankAccount = null;
        }
    },
    extraReducers: (builder) => {
        // =========== GET ALL BANK ACCOUNTS BY CASE ID ===========
        builder
            .addCase(getAllBankAccountsByCaseIdAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(getAllBankAccountsByCaseIdAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.bankAccounts = action.payload.bankAccounts;
                state.assetOwned = action.payload.assetOwnedBy;
                state.message = action.payload.message;
            })
            .addCase(getAllBankAccountsByCaseIdAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to fetch bank accounts";
                state.bankAccounts = [];
            })

        // =========== GET SINGLE BANK ACCOUNT BY ID ===========
        builder
            .addCase(getBankAccountByIdAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(getBankAccountByIdAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.selectedBankAccount = action.payload.bankAccount;
                if (action.payload.assetOwnedBy) {
                    state.assetOwned = action.payload.assetOwnedBy;
                }
                state.message = action.payload.message;
            })
            .addCase(getBankAccountByIdAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to fetch bank account";
                state.selectedBankAccount = null;
            })

        // =========== CREATE NEW BANK ACCOUNT ===========
        builder
            .addCase(createBankAccountAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(createBankAccountAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.bankAccounts.unshift(action.payload.bankAccount);
                state.message = action.payload.message;
            })
            .addCase(createBankAccountAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to create bank account";
            })

        // =========== UPDATE BANK ACCOUNT ===========
        builder
            .addCase(updateBankAccountAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(updateBankAccountAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;

                const index = state.bankAccounts.findIndex(
                    b => b.bankAccountID === action.payload.bankAccountID
                );
                if (index !== -1) {
                    state.bankAccounts[index] = action.payload.bankAccount;
                }

                if (state.selectedBankAccount?.bankAccountID === action.payload.bankAccountID) {
                    state.selectedBankAccount = action.payload.bankAccount;
                }
                state.message = action.payload.message;
            })
            .addCase(updateBankAccountAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to update bank account";
            })

        // =========== DELETE BANK ACCOUNT ===========
        builder
            .addCase(deleteBankAccountAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(deleteBankAccountAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;

                state.bankAccounts = state.bankAccounts.filter(
                    b => b.bankAccountID !== action.payload.bankAccountID
                );

                if (state.selectedBankAccount?.bankAccountID === action.payload.bankAccountID) {
                    state.selectedBankAccount = null;
                }
                state.message = action.payload.message;
            })
            .addCase(deleteBankAccountAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to delete bank account";
            })

        // =========== SEARCH BANK ACCOUNTS ===========
        builder
            .addCase(searchBankAccountsAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(searchBankAccountsAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.bankAccounts = action.payload.bankAccounts;
                state.filters = action.payload.searchCriteria;
                state.message = action.payload.message;
            })
            .addCase(searchBankAccountsAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to search bank accounts";
                state.bankAccounts = [];
            })

        // =========== BULK CREATE BANK ACCOUNTS ===========
        builder
            .addCase(bulkCreateBankAccountsAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(bulkCreateBankAccountsAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;

                state.bankAccounts = [...action.payload.bankAccounts, ...state.bankAccounts];
                state.message = action.payload.message;
            })
            .addCase(bulkCreateBankAccountsAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to bulk create bank accounts";
            })

        // =========== GET BANK ACCOUNTS WITH PAGINATION ===========
        builder
            .addCase(getBankAccountsWithPaginationAction.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.success = false;
            })
            .addCase(getBankAccountsWithPaginationAction.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.bankAccounts = action.payload.bankAccounts;
                state.pagination = action.payload.pagination;
                state.message = action.payload.message;
            })
            .addCase(getBankAccountsWithPaginationAction.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload?.message || "Failed to fetch bank accounts";
            })

        // =========== EXPORT BANK ACCOUNTS TO EXCEL ===========
        builder
            .addCase(exportBankAccountsToExcelAction.pending, (state) => {
                state.exportLoading = true;
                state.exportError = null;
            })
            .addCase(exportBankAccountsToExcelAction.fulfilled, (state, action) => {
                state.exportLoading = false;
                state.exportData = action.payload.blobData;
                state.message = action.payload.message;
            })
            .addCase(exportBankAccountsToExcelAction.rejected, (state, action) => {
                state.exportLoading = false;
                state.exportError = action.payload?.message || "Failed to export bank accounts";
            })

        // =========== MATCHERS ===========
        builder
            .addMatcher(
                (action) => action.type === clearBankAccountStateAction.type,
                () => initialState
            )
            .addMatcher(
                (action) => action.type === setSelectedBankAccountAction.type,
                (state, action) => {
                    state.selectedBankAccount = action.payload;
                }
            )
            .addMatcher(
                (action) => action.type === setBankAccountFiltersAction.type,
                (state, action) => {
                    state.filters = action.payload;
                }
            );
    }
});

export const {
    resetBankAccountState,
    clearBankAccountError,
    updateBankAccountInList,
    removeBankAccountFromList,
    clearSelectedBankAccount
} = bankAccountSlice.actions;

export default bankAccountSlice.reducer;
